import type { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { useEffect, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export interface UseTablePaginationStateOptions {
  defaultPageSize?: number;
}

export interface UseTablePaginationStateReturn {
  pagination: PaginationState;
  apiPage: number;
  setPagination: OnChangeFn<PaginationState>;
}

export const DEFAULT_PAGE_SIZE = 10;

export function useTablePaginationState({
  defaultPageSize = DEFAULT_PAGE_SIZE,
}: UseTablePaginationStateOptions = {}): UseTablePaginationStateReturn {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get pagination state from URL
  const pagination = useMemo<PaginationState>(() => {
    const pageParam = searchParams.get('page');
    const pageSizeParam = searchParams.get('pageSize');

    const page = pageParam ? Math.max(0, parseInt(pageParam, 10) - 1) : 0;
    const pageSize = pageSizeParam ? parseInt(pageSizeParam, 10) : defaultPageSize;

    return {
      pageIndex: page,
      pageSize: pageSize,
    };
  }, [searchParams, defaultPageSize]);

  // Update URL when pagination changes
  const setPagination = (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
    const params = new URLSearchParams(searchParams);

    if (newPagination.pageIndex === 0) {
      params.delete('page');
    } else {
      params.set('page', (newPagination.pageIndex + 1).toString());
    }

    if (newPagination.pageSize === defaultPageSize) {
      params.delete('pageSize');
    } else {
      params.set('pageSize', newPagination.pageSize.toString());
    }

    setSearchParams(params, { replace: true, state: location.state });
  };

  // Store current path globally after hook has read previous path
  useEffect(() => {
    try {
      sessionStorage.setItem('pagination-previous-path', location.pathname);
    } catch (error) {
      console.warn('Failed to store previous path:', error);
    }
  }, [location.pathname]);

  const apiPage = pagination.pageIndex + 1;

  return {
    pagination,
    apiPage,
    setPagination,
  };
}
